import { useState } from 'react'
import { motion } from 'framer-motion'
import { Zap, Shield, Brain, BookOpen, ChevronRight, ArrowRight } from 'lucide-react'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'

const brandFont = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", -apple-system, sans-serif',
  fontWeight: 100,
} as const

const bodyFont = {
  fontFamily: '"Helvetica Neue", sans-serif',
  fontWeight: 300,
} as const

const monoFont = {
  fontFamily: '"JetBrains Mono", "Fira Code", "Monaco", Menlo, Consolas, monospace',
  fontWeight: 400,
} as const

const DEMOS = [
  {
    id: 'reasoning',
    icon: Brain,
    label: 'MATRIZ Reasoning',
    endpoint: '/v1/reasoning',
    description: 'Submit a query and receive a full reasoning graph.',
    exampleInput: 'Should I deploy a new model version with 5% accuracy gain but 20% latency increase?',
    exampleOutput: `{
  "answer": "Conditional yes — depends on use-case latency tolerance.",
  "confidence": 0.84,
  "reasoning_graph": {
    "nodes": [
      { "id": "N1", "type": "premise", "content": "5% accuracy gain is measurable and material." },
      { "id": "N2", "type": "premise", "content": "20% latency increase may violate SLA thresholds." },
      { "id": "N3", "type": "inference", "content": "Accuracy vs latency trade-off requires context." },
      { "id": "N4", "type": "conclusion", "content": "Deploy to canary if p95 latency remains < 250ms." }
    ],
    "edges": [["N1","N3"],["N2","N3"],["N3","N4"]]
  },
  "guardian_check": { "passed": true, "policy": "default-ethics-v1" }
}`,
  },
  {
    id: 'guardian',
    icon: Shield,
    label: 'Guardian Ethics',
    endpoint: '/v1/guardian',
    description: 'Check text against constitutional AI ethical policy.',
    exampleInput: 'Generate marketing copy that implies our product cures all diseases.',
    exampleOutput: `{
  "passed": false,
  "violations": [
    {
      "rule": "no-false-medical-claims",
      "severity": "critical",
      "segment": "cures all diseases",
      "remediation": "Replace with evidence-backed benefit claims only."
    }
  ],
  "drift_score": 0.82,
  "recommendation": "Rewrite with qualified, verifiable health benefits."
}`,
  },
  {
    id: 'memory',
    icon: BookOpen,
    label: 'Memory Fold',
    endpoint: '/v1/memory/fold',
    description: 'Store a symbolic memory fold and retrieve provenance.',
    exampleInput: 'Store: user prefers concise explanations, dislikes jargon, context: onboarding session.',
    exampleOutput: `{
  "fold_id": "fold_9kLm2pQr",
  "stored": true,
  "timestamp": "2026-03-19T14:22:11Z",
  "signature": "sha256:a3f9...",
  "cascade_risk": 0.03,
  "provenance": {
    "source": "onboarding_session",
    "session_id": "sess_xKj4",
    "confirmed_by": "guardian"
  }
}`,
  },
]

export default function PlaygroundPage() {
  const [activeDemo, setActiveDemo] = useState(DEMOS[0].id)
  const demo = DEMOS.find((d) => d.id === activeDemo)!

  return (
    <div className="min-h-screen bg-consciousness-deep text-awareness-silver">
      <SiteHeader />

      <section className="pt-32 pb-16 px-6 relative overflow-hidden">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[300px] pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 50% 0%, rgba(13,148,136,0.1) 0%, transparent 70%)',
          }}
        />
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-vision-teal/10 border border-vision-teal/25 mb-6">
              <Zap className="w-3 h-3 text-vision-teal" />
              <span className="text-vision-teal text-xs tracking-[0.15em] uppercase" style={bodyFont}>
                No auth required
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl text-white tracking-[0.12em] mb-4" style={brandFont}>
              API Playground
            </h1>
            <p className="text-xl text-white/55 max-w-2xl mx-auto" style={bodyFont}>
              Explore LUKHAS APIs live. Select an endpoint, inspect the request, and see a
              real-world response format.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Demo selector tabs */}
          <div className="flex gap-2 mb-8 flex-wrap">
            {DEMOS.map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                onClick={() => setActiveDemo(id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm border transition-all ${
                  activeDemo === id
                    ? 'bg-vision-teal/15 border-vision-teal/40 text-vision-teal'
                    : 'bg-white/[0.03] border-white/[0.1] text-white/50 hover:border-white/25 hover:text-white/70'
                }`}
                style={bodyFont}
              >
                <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                {label}
              </button>
            ))}
          </div>

          <motion.div
            key={activeDemo}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="grid md:grid-cols-2 gap-6"
          >
            {/* Request panel */}
            <div className="rounded-2xl border border-white/[0.09] bg-white/[0.03] overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-3 border-b border-white/[0.07] bg-white/[0.02]">
                <span className="text-[10px] px-2 py-0.5 rounded bg-vision-teal/20 text-vision-teal font-medium tracking-wide" style={monoFont}>
                  POST
                </span>
                <code className="text-xs text-white/60" style={monoFont}>
                  api.lukhas.io{demo.endpoint}
                </code>
              </div>
              <div className="p-5">
                <p className="text-xs text-white/40 mb-3" style={bodyFont}>
                  {demo.description}
                </p>
                <div className="rounded-xl border border-white/[0.07] bg-consciousness-deep/80 p-4">
                  <p className="text-xs text-white/30 mb-2 uppercase tracking-wider" style={bodyFont}>
                    Example input
                  </p>
                  <p className="text-sm text-white/70 leading-relaxed" style={bodyFont}>
                    "{demo.exampleInput}"
                  </p>
                </div>
                <div className="mt-4 p-3 rounded-lg bg-vision-teal/5 border border-vision-teal/15">
                  <p className="text-xs text-vision-teal/70" style={bodyFont}>
                    Public endpoint — no Authorization header needed for testing.
                  </p>
                </div>
              </div>
            </div>

            {/* Response panel */}
            <div className="rounded-2xl border border-white/[0.09] bg-white/[0.03] overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.07] bg-white/[0.02]">
                <span className="text-xs text-white/40 uppercase tracking-wider" style={bodyFont}>
                  Response
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded bg-success-green/15 text-success-green border border-success-green/25" style={bodyFont}>
                  200 OK
                </span>
              </div>
              <pre className="p-5 text-xs text-white/65 overflow-x-auto leading-relaxed max-h-96" style={monoFont}>
                <code>{demo.exampleOutput}</code>
              </pre>
            </div>
          </motion.div>

          {/* Callout to full docs */}
          <div className="mt-8 flex items-center justify-between rounded-xl border border-white/[0.07] bg-white/[0.02] p-5">
            <div>
              <p className="text-sm text-white/70" style={bodyFont}>
                Ready to integrate?
              </p>
              <p className="text-xs text-white/35 mt-0.5" style={bodyFont}>
                Full API reference, SDKs, and authentication guide at lukhas.dev
              </p>
            </div>
            <div className="flex gap-3">
              <a
                href="https://lukhas.id"
                className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-vision-teal/15 border border-vision-teal/30 text-vision-teal text-xs tracking-wide hover:bg-vision-teal/25 transition-all"
                style={bodyFont}
              >
                Get API Key <ChevronRight className="w-3 h-3" />
              </a>
              <a
                href="https://lukhas.dev"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-white/50 text-xs tracking-wide hover:border-white/30 hover:text-white/70 transition-all"
                style={bodyFont}
              >
                Full Docs <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  )
}
